import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  AccessNotice,
  AddItemBar,
  Checkbox,
  Chip,
  Dialog,
  EmptyState,
  ErrorBanner,
  GroupCard,
  ItemsSkeleton,
  SectionHeader,
  useToast,
} from '../components';
import { buildSections, categoriesOf, moveTarget } from '../api/sections';
import {
  useActiveList,
  useAddItem,
  useClearDone,
  useDeleteItem,
  useListDetail,
  useMoveItem,
  useSetActiveList,
  useToggleItem,
} from '../api/queries';
import { ApiError, isAccessError } from '../api/client';
import { useSurface } from '../hooks/useSurface';
import { EditItemDialog, RenameCategoryDialog, SetCategoryDialog } from './ItemDialogs';
import type { Item } from '../api/types';

/** Mirrors the bot's `/show` `/todo` `/done` triple — see the note on `filter` below. */
type Filter = 'all' | 'todo' | 'done';

const FILTERS: Array<{ value: Filter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'todo', label: 'To do' },
  { value: 'done', label: 'Done' },
];

/**
 * A list and its items.
 *
 * The viewer role is a *distinct render*, not a disabled one: no add bar, no
 * checkboxes, no row menus, plus a `👁 view only` pill. Nothing here is
 * `disabled` — the affordances simply are not in the tree. That is the whole
 * point: `service.py` will 403 a viewer's write, so a control that could
 * produce one must not exist, and a control that is merely greyed out is still
 * a control someone can re-enable in devtools.
 *
 * Roles arrive from the server with every read, so a role change (or a removed
 * share) collapses this screen to `AccessNotice` on the next refetch rather
 * than leaving a stale editor UI pointed at a list the user lost.
 */
export function DetailScreen({ listId }: { listId: number }) {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { isDesktop, isWebMobile } = useSurface();
  const { list, isPending, isError, error, refetch } = useListDetail(listId);
  const { data: active } = useActiveList();
  const addItem = useAddItem(listId);
  const toggleItem = useToggleItem(listId);
  const deleteItem = useDeleteItem(listId);
  const moveItem = useMoveItem(listId);
  const clearDone = useClearDone(listId);
  const setActive = useSetActiveList();
  const [menuItem, setMenuItem] = useState<Item | null>(null);
  // The item being edited, held separately from `menuItem` so the ⋯ sheet can
  // close as the dialog opens instead of stacking two sheets on top of a row.
  const [editItem, setEditItem] = useState<Item | null>(null);
  const [categoryItem, setCategoryItem] = useState<Item | null>(null);
  // The category being renamed, held as its stored spelling rather than as a
  // section: sections are rebuilt from the (filtered) items on every render, so
  // a section object would go stale the moment the rename lands.
  const [renameCategory, setRenameCategory] = useState<string | null>(null);
  const [listMenuOpen, setListMenuOpen] = useState(false);
  // Three states, not a done/not-done toggle: two toggles leave no way back to
  // "everything", and the bot already trained the vocabulary with /show /todo
  // /done. Kept local — a filter is a way of looking, not part of the list.
  const [filter, setFilter] = useState<Filter>('all');

  const isViewer = list?.role === 'viewer';
  const isOwner = list?.role === 'owner';
  const canWrite = !!list && !isViewer;
  const items = list?.items ?? [];
  // Filter *before* grouping, so a category with nothing matching drops out
  // entirely instead of rendering a heading over an empty card.
  const visible = items.filter((i) => filter === 'all' || (filter === 'done') === i.done);
  const sections = buildSections(visible);
  const isEmpty = !isPending && !isError && items.length === 0;
  // Distinct from `isEmpty`: the list has items, this *view* of them has none.
  // Sharing one branch would offer "add your first item" on a full list.
  const isFilteredEmpty = !isPending && !isError && !isEmpty && visible.length === 0;
  // Counts stay on the unfiltered items — under a filter, "Clear 3 done" must
  // still mean every done item on the list, which is what the server clears.
  const doneCount = items.filter((i) => i.done).length;
  const isActive = active?.listId === listId;

  // `items`, the **unfiltered** list — not `visible`, and above all not
  // `sections`. The row above this one on screen is not the row above it on the
  // server: `buildSections` has already sorted done items to the bottom of each
  // section, and a filter can leave a row from another category sitting
  // directly above one whose real neighbours are all hidden. `moveTarget`
  // answers from the item's own `(category, done)` bucket instead, which is the
  // only unit `position` orders within and the only move the server accepts.
  //
  // It is also why that endpoint takes an id and not an index: an index needs
  // both ends to agree on the order, and they openly do not — the server sorts
  // uncategorized last, `buildSections` renders it first. See `moveTarget` for
  // why the *unfiltered* array specifically.
  const moveUp = menuItem ? moveTarget(items, menuItem, 'up') : null;
  const moveDown = menuItem ? moveTarget(items, menuItem, 'down') : null;

  /** Write failures are the user's business — say so, since the row snapped back. */
  const writeFailed = (fallback: string) => (err: unknown) => {
    if (err instanceof ApiError && err.isForbidden) {
      showToast('You only have view access to this list');
      void refetch();
      return;
    }
    showToast(err instanceof ApiError ? err.message : fallback);
  };

  /** No success toast: the row is somewhere else, which says it better than words. */
  const move = (afterId: number | null) => {
    if (menuItem) {
      moveItem.mutate(
        { itemId: menuItem.id, afterId },
        { onError: writeFailed("Couldn't move that item") },
      );
    }
    setMenuItem(null);
  };

  const overflowButton = (
    <button
      type="button"
      className="item-row__menu"
      aria-label="List actions"
      onClick={() => setListMenuOpen(true)}
      style={{ fontSize: 22 }}
    >
      ⋯
    </button>
  );

  // A 403/404 is final: no retry, no skeleton, and the list is never named.
  if (isAccessError(error)) {
    return (
      <div className={isDesktop ? 'screen-content content' : 'screen-content'}>
        <AccessNotice
          kind={error.isForbidden ? 'forbidden' : 'missing'}
          onBack={() => navigate('/', { replace: true })}
        />
      </div>
    );
  }

  return (
    <>
      {isDesktop ? (
        <div className="detail-title">
          <span className="detail-title__text">{list?.name ?? ' '}</span>
          {isViewer ? <span className="viewonly-tag">View only</span> : null}
          <div style={{ flex: 1 }} />
          {overflowButton}
        </div>
      ) : null}

      {isWebMobile ? (
        // web-mobile header is title + ⋯ by design; whether it gains a back
        // chevron is an open question the user has not answered, so it stays
        // exactly as PR6 shipped it.
        <header className="app-header">
          <div className="app-header__title app-header__title--sub">{list?.name ?? ' '}</div>
          {isViewer ? <span className="viewonly-tag">View only</span> : null}
          <div className="app-header__spacer" />
          {overflowButton}
        </header>
      ) : null}

      {!isDesktop && !isWebMobile && isViewer ? (
        <div className="viewonly-pill">
          <span>👁 view only</span>
        </div>
      ) : null}

      {isError ? (
        <ErrorBanner message={error?.message} onRetry={() => refetch()} />
      ) : null}

      <div className={isDesktop ? 'screen-content content' : 'screen-content'}>
        {isPending ? <ItemsSkeleton /> : null}

        {!isPending && items.length > 0 ? (
          // Screen-local on purpose. The visual sibling is ShareScreen's
          // `share__roleTabs` — same token pair, same `.is-active` — but a
          // segmented control with two callers is not yet a primitive; a third
          // instance is when this and that one get extracted together. The ARIA
          // is what this one adds: `share__roleTabs` is bare buttons, so a
          // screen reader never hears which segment is chosen.
          <div className="detail-filter" role="radiogroup" aria-label="Filter items">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                role="radio"
                aria-checked={filter === f.value}
                className={filter === f.value ? 'detail-filter__tab is-active' : 'detail-filter__tab'}
                onClick={() => setFilter(f.value)}
              >
                {f.label}
              </button>
            ))}
          </div>
        ) : null}

        {isFilteredEmpty ? (
          <EmptyState
            small
            glyph={filter === 'done' ? '○' : '✓'}
            title={filter === 'done' ? 'Nothing ticked off yet' : 'All done here'}
            body={
              filter === 'done'
                ? 'No items on this list are done. Tick one off and it shows up here.'
                : 'Every item on this list is done. Switch to All to see them.'
            }
          />
        ) : null}

        {isEmpty ? (
          <EmptyState
            small
            glyph="✓"
            title="Nothing here yet"
            body={
              isViewer ? (
                'This list is empty.'
              ) : (
                <>
                  Add your first item below. Use{' '}
                  <b style={{ color: 'var(--tg-accent-text)' }}>#tag</b> to sort it into a category.
                </>
              )
            }
          />
        ) : null}

        {!isPending && visible.length > 0 ? (
          <div style={{ padding: '6px 0 24px' }}>
            {sections.map((section) => (
              <div key={section.key}>
                {section.label ? (
                  // The heading is the handle for the whole category, which is
                  // what a rename acts on — there is no other object on this
                  // screen that *is* the category. A viewer gets the plain
                  // caption back, not a disabled button: rename-category is
                  // editor-or-owner on the server, so `canWrite` is the gate,
                  // and the affordance is absent rather than inert.
                  // `section.key` is the stored spelling; `section.label` is it
                  // uppercased for display, and the server must be sent the
                  // former.
                  <SectionHeader
                    label={canWrite ? `Rename category ${section.key}` : undefined}
                    onClick={canWrite ? () => setRenameCategory(section.key) : undefined}
                  >
                    {section.label}
                  </SectionHeader>
                ) : null}
                <GroupCard>
                  {section.items.map((item) => (
                    <div className="item-row" key={item.id}>
                      {isViewer ? null : (
                        <Checkbox
                          checked={item.done}
                          label={item.text}
                          onChange={() =>
                            toggleItem.mutate(
                              { itemId: item.id, done: !item.done },
                              { onError: writeFailed("Couldn't save that change") },
                            )
                          }
                        />
                      )}
                      <span className={item.done ? 'item-row__text is-done' : 'item-row__text'}>
                        {item.text}
                      </span>
                      {item.category ? <Chip>#{item.category.toLowerCase()}</Chip> : null}
                      {isViewer ? null : (
                        <button
                          type="button"
                          className="item-row__menu"
                          aria-label={`Actions for ${item.text}`}
                          onClick={() => setMenuItem(item)}
                        >
                          ⋯
                        </button>
                      )}
                    </div>
                  ))}
                </GroupCard>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      {canWrite && !isPending ? (
        <AddItemBar
          onAdd={(text) =>
            addItem.mutate(text, {
              onSuccess: () => showToast('Item added'),
              onError: writeFailed("Couldn't add that item"),
            })
          }
        />
      ) : null}

      <Dialog open={!!menuItem} onDismiss={() => setMenuItem(null)}>
        <div className="dialog__head">
          <div className="dialog__title">{menuItem?.text}</div>
        </div>
        <div className="dialog__actions dialog__actions--stacked">
          <button
            type="button"
            className="dialog__btn dialog__btn--cancel-lg"
            onClick={() => {
              setEditItem(menuItem);
              setMenuItem(null);
            }}
          >
            Edit text
          </button>
          <button
            type="button"
            className="dialog__btn dialog__btn--cancel-lg"
            onClick={() => {
              setCategoryItem(menuItem);
              setMenuItem(null);
            }}
          >
            Change category
          </button>
          {/* Absent, not disabled, at either end of the section — the server
              400s a move out of a `(category, done)` bucket, and a control that
              can only ever error should not be in the tree. Same rule the
              viewer render follows. `canWrite` because moving is an item write,
              which is editor-or-owner. */}
          {canWrite && moveUp ? (
            <button
              type="button"
              className="dialog__btn dialog__btn--cancel-lg"
              onClick={() => move(moveUp.afterId)}
            >
              Move up
            </button>
          ) : null}
          {canWrite && moveDown ? (
            <button
              type="button"
              className="dialog__btn dialog__btn--cancel-lg"
              onClick={() => move(moveDown.afterId)}
            >
              Move down
            </button>
          ) : null}
          <button
            type="button"
            className="dialog__btn dialog__btn--destructive"
            onClick={() => {
              if (menuItem) {
                deleteItem.mutate(menuItem.id, {
                  onSuccess: () => showToast('Item deleted'),
                  onError: writeFailed("Couldn't delete that item"),
                });
              }
              setMenuItem(null);
            }}
          >
            Delete item
          </button>
          <button
            type="button"
            className="dialog__btn dialog__btn--cancel-lg"
            onClick={() => setMenuItem(null)}
          >
            Cancel
          </button>
        </div>
      </Dialog>

      {/* canWrite, not isOwner: `PATCH /api/items/{id}` is editor-or-owner, so
          gating this any tighter would hide a control an editor may use. Mounted
          only while an item is chosen, which is what seeds the field from that
          item's text without an effect. */}
      {canWrite && editItem ? (
        <EditItemDialog listId={listId} item={editItem} onClose={() => setEditItem(null)} />
      ) : null}

      {/* Same editor-or-owner gate, same reason. The choices come off the
          unfiltered items: a category is no less real for having nothing
          matching the current filter, and picking it is how an item gets
          back into view. */}
      {canWrite && categoryItem ? (
        <SetCategoryDialog
          listId={listId}
          item={categoryItem}
          categories={categoriesOf(items)}
          onClose={() => setCategoryItem(null)}
        />
      ) : null}

      {/* Same editor-or-owner gate as the two above. Mounted only while a
          category is chosen, which is what seeds the field from that category's
          spelling without an effect. */}
      {canWrite && renameCategory ? (
        <RenameCategoryDialog
          listId={listId}
          category={renameCategory}
          onClose={() => setRenameCategory(null)}
        />
      ) : null}

      <Dialog open={listMenuOpen} onDismiss={() => setListMenuOpen(false)}>
        <div className="dialog__head">
          <div className="dialog__title">{list?.name}</div>
        </div>
        <div className="dialog__actions dialog__actions--stacked">
          {isActive ? null : (
            <button
              type="button"
              className="dialog__btn dialog__btn--cancel-lg"
              onClick={() => {
                setListMenuOpen(false);
                setActive.mutate(listId, {
                  onSuccess: () => showToast('Active list set — the bot will use it too'),
                  onError: writeFailed("Couldn't set the active list"),
                });
              }}
            >
              Make this my active list
            </button>
          )}
          {/* Owner-only, not canWrite: renaming and sharing are the owner's
              alone on the server, so an editor seeing these would be shown
              controls that 403 on tap. Share is the worse of the two — the
              screen behind it lists co-members' Telegram user ids. */}
          {isOwner ? (
            <>
              <button
                type="button"
                className="dialog__btn dialog__btn--cancel-lg"
                onClick={() => {
                  setListMenuOpen(false);
                  navigate(`/lists/${listId}/rename`);
                }}
              >
                Rename list
              </button>
              <button
                type="button"
                className="dialog__btn dialog__btn--cancel-lg"
                onClick={() => {
                  setListMenuOpen(false);
                  navigate(`/lists/${listId}/share`);
                }}
              >
                Share
              </button>
            </>
          ) : null}
          {/* Clearing done items *is* an editor's to do — writer gate is right. */}
          {canWrite ? (
            <>
              {doneCount > 0 ? (
                <button
                  type="button"
                  className="dialog__btn dialog__btn--cancel-lg"
                  onClick={() => {
                    setListMenuOpen(false);
                    clearDone.mutate(undefined, {
                      onSuccess: (result) =>
                        showToast(
                          result.removed === 1 ? '1 done item cleared' : `${result.removed} done items cleared`,
                        ),
                      onError: writeFailed("Couldn't clear done items"),
                    });
                  }}
                >
                  Clear {doneCount} done
                </button>
              ) : null}
            </>
          ) : null}
          {isOwner ? (
            <button
              type="button"
              className="dialog__btn dialog__btn--destructive"
              onClick={() => {
                setListMenuOpen(false);
                navigate(`/lists/${listId}/delete`);
              }}
            >
              Delete list
            </button>
          ) : null}
          <button
            type="button"
            className="dialog__btn dialog__btn--cancel-lg"
            onClick={() => setListMenuOpen(false)}
          >
            Cancel
          </button>
        </div>
      </Dialog>
    </>
  );
}
